 import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DayPicker, { DateUtils } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'react-day-picker/lib/style.css';
import UserStore from '../stores/UserStore';
import { I18nFormatter } from '../utils/I18n/I18n';

const _getDate = (date) => {
    return date instanceof moment ? date.toDate() : date;
};

 const DayPickerInputRange = ({ from: propFrom, to: propTo, onDayChange }) => {
    const leftMonth = useRef(null);
    const rightMonth = useRef(null);
    const locale = UserStore.getLocale();

    const format = useCallback((d) => I18nFormatter.date(d), []);
    const [from, setFrom] = useState(_getDate(propFrom));
    const [to, setTo] = useState(_getDate(propTo));
    const [enteredTo, setEnteredTo] = useState(_getDate(propTo));
    const [valueFrom, setValueFrom] = useState(format(_getDate(propFrom)));
    const [valueTo, setValueTo] = useState(format(_getDate(propTo)));

    useEffect(() => {
        const f = _getDate(propFrom);
        const t = _getDate(propTo);
        setFrom(f); setTo(t); setEnteredTo(t); setValueFrom(format(f)); setValueTo(format(t));
        if (leftMonth.current?.showMonth) leftMonth.current.showMonth(f);
        if (rightMonth.current?.showMonth) rightMonth.current.showMonth(moment(f).add(1, 'months').toDate());
    }, [propFrom, propTo, format]);

    const isSelectingFirstDay = useCallback((f, t, day) => {
        const isBeforeFirstDay = f && DateUtils.isDayBefore(day, f);
        const isRangeSelected = f && t;
        return !f || isBeforeFirstDay || isRangeSelected;
    }, []);

    const emitChange = useCallback((f, t) => { onDayChange && onDayChange(moment(f), moment(t)); }, [onDayChange]);

    const handleDayClick = useCallback((day) => {
        if (isSelectingFirstDay(from, to, day)) {
            setFrom(day); setTo(null); setEnteredTo(null); setValueFrom(format(day));
        } else {
            setTo(day); setEnteredTo(day); setValueTo(format(day)); emitChange(from, day);
        }
    }, [from, to, isSelectingFirstDay, format, emitChange]);

    const prevShowMonths = useCallback(() => { leftMonth.current?.showPreviousMonth?.(); rightMonth.current?.showPreviousMonth?.(); }, []);
    const nextShowMonths = useCallback(() => { leftMonth.current?.showNextMonth?.(); rightMonth.current?.showNextMonth?.(); }, []);

    const NavbarLeft = useCallback(({ className }) => (
        <div className={className}>
            <span className="DayPicker-NavButton ranges-calendar__nav-left" onClick={prevShowMonths}>
                <i className="fa fa-chevron-left" />
            </span>
        </div>
    ), [prevShowMonths]);
    const NavbarRight = useCallback(({ className }) => (
        <div className={className}>
            <span className="DayPicker-NavButton ranges-calendar__nav-right" onClick={nextShowMonths}>
                <i className="fa fa-chevron-right" />
            </span>
        </div>
    ), [nextShowMonths]);

    return (
        <div className="reactdaypicker">
            <div className="reactdaypicker-item">
                <div className="reactdaypicker-field">
                    <input className="form-control reactdaypicker-field__input" type="text" value={valueFrom} readOnly />
                    <i className="fa fa-calendar glyphicon glyphicon-calendar reactdaypicker-field__icon" />
                    <div className="calendar-time" />
                </div>
                <DayPicker ref={leftMonth} className={'reactdaypicker__range'} numberOfMonths={1} locale={locale} localeUtils={MomentLocaleUtils} navbarElement={NavbarLeft} selectedDays={[from, { from, to: enteredTo }]} modifiers={{ start: from, end: enteredTo }} onDayClick={handleDayClick} />
            </div>
            <div className="reactdaypicker-item">
                <div className="reactdaypicker-field">
                    <input className="form-control reactdaypicker-field__input" type="text" value={valueTo} readOnly />
                    <i className="fa fa-calendar glyphicon glyphicon-calendar reactdaypicker-field__icon" />
                    <div className="calendar-time" />
                </div>
                <DayPicker ref={rightMonth} className={'reactdaypicker__range'} numberOfMonths={1} navbarElement={NavbarRight} initialMonth={moment().add(1, 'months').toDate()} locale={locale} localeUtils={MomentLocaleUtils} selectedDays={[from, { from, to: enteredTo }]} modifiers={{ start: from, end: enteredTo }} onDayClick={handleDayClick} />
            </div>
        </div>
    );
 };

 DayPickerInputRange.propTypes = { from: PropTypes.any, to: PropTypes.any, onDayChange: PropTypes.func };

 DayPickerInputRange.defaultProps = {};


 export default DayPickerInputRange;
